// PRE RMA CSB — Gestion de l'age.
// Reproduit exactement la logique des colonnes AE/AF/AG/AH/N/W de Saisie_Unique
// (Phase1 3.2). Le resultat ageJours est LE champ pivot: aucune autre regle
// du systeme ne doit reparser une chaine de caracteres pour calculer un age.
//
// Les BORNES (min/max par unité, jours par unité) viennent de RULES.age
// (rules.config.ts) — module de configuration unique. Seule la syntaxe de
// reconnaissance des suffixes texte ("jours"/"mois"/"ans"/"j"/"m"/"a") reste
// ici : ce n'est pas une règle métier chiffrée, mais un format de saisie.

import { RULES } from './rules.config.ts';
import type { AgeUnite } from './rules.config.ts';

export interface AgeSaisi {
  valeur: number;
  unite: AgeUnite;
}

export interface AgeParseResult {
  ok: boolean;
  ageSaisi?: AgeSaisi;
  ageJours?: number;
  erreur?: string;
}

const UNITE_SUFFIXES: Array<{ suffix: string; unite: AgeUnite }> = [
  { suffix: 'jours', unite: 'jours' },
  { suffix: 'jour', unite: 'jours' },
  { suffix: 'mois', unite: 'mois' },
  { suffix: 'ans', unite: 'ans' },
  { suffix: 'an', unite: 'ans' },
  { suffix: 'j', unite: 'jours' },
  { suffix: 'm', unite: 'mois' },
  { suffix: 'a', unite: 'ans' },
];

/**
 * Normalise une saisie brute d'age (equivalent colonne AE).
 * Accepte "12 mois", "39,mois", "5 ans", "15 j", etc.
 */
function normaliser(brut: string): string {
  return brut
    .replace(/[\u00A0\u200B-\u200D\uFEFF]/g, ' ') // espaces insécables / largeur nulle d'un copier-coller
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/\.+$/, '') // point final : « 12 mois. »
    .replace(',jours', 'jours')
    .replace(',mois', 'mois')
    .replace(',ans', 'ans')
    .replace(',j', 'j')
    .replace(',m', 'm')
    .replace(',an', 'an')
    .replace(',a', 'a');
}

/**
 * Parse une saisie brute d'age en {valeur, unite} + validite (equivalent
 * colonnes AG/AF/AH/W). Applique les bornes strictes de RULES.age
 * (rules.config.ts) :
 *   - jours: RULES.age.bornesParUnite.jours
 *   - mois : RULES.age.bornesParUnite.mois
 *   - ans  : RULES.age.bornesParUnite.ans
 */
export function parseAge(brutEntree: string | null | undefined): AgeParseResult {
  // Une valeur non texte (nombre collé depuis un tableur, par ex.) ne doit jamais faire planter le calcul.
  const brut = brutEntree === null || brutEntree === undefined ? brutEntree : String(brutEntree);
  if (brut === null || brut === undefined || brut.replace(/[\u00A0\u200B-\u200D\uFEFF]/g, ' ').trim() === '') {
    return { ok: false, erreur: 'Âge vide' };
  }
  const norm = normaliser(brut);

  const match = UNITE_SUFFIXES.find(({ suffix }) => norm.endsWith(suffix));
  if (!match) {
    return { ok: false, erreur: 'Unité d\'âge non reconnue (jours/mois/ans attendu)' };
  }
  const numPart = norm.slice(0, norm.length - match.suffix.length);
  const valeur = Number(numPart);
  if (numPart === '' || Number.isNaN(valeur)) {
    return { ok: false, erreur: 'Partie numérique invalide' };
  }

  const bornes = RULES.age.bornesParUnite[match.unite];
  const bornesOk = valeur >= bornes.min && valeur <= bornes.max;

  if (!bornesOk) {
    return {
      ok: false,
      erreur:
        'Âge invalide : utiliser jours (<2 mois), mois (2 à <60 mois) ou années (≥5 ans), une seule unité.',
    };
  }

  const ageJours = Math.round(valeur * RULES.age.joursParUnite[match.unite]);
  return { ok: true, ageSaisi: { valeur, unite: match.unite }, ageJours };
}

/** Reconstruit une chaine d'affichage a partir de {valeur, unite}. */
export function formatAge(ageSaisi: AgeSaisi): string {
  const suffixe = ageSaisi.unite === 'jours' ? 'jours' : ageSaisi.unite === 'mois' ? 'mois' : 'ans';
  return `${ageSaisi.valeur} ${suffixe}`;
}
